import { SlideshowSelectEvent } from '@theme/events';

/**
 * Carousel navigation arrows that live in a section header and control a
 * `slideshow-component` located elsewhere in the DOM, targeted by id.
 *
 * The click itself is handled natively by the theme's `on:click="#id/next"`
 * delegation (see component.js). This element's only job is to mirror the
 * slideshow's boundary state onto its two buttons, so the arrow that can't move
 * appears disabled — matching the built-in arrows' behaviour (slideshow.js).
 */
class SlideshowHeaderNav extends HTMLElement {
  /** @type {MutationObserver | null} */
  #domObserver = null;

  connectedCallback() {
    document.addEventListener(SlideshowSelectEvent.eventName, this.#handleSelect);
    this.#syncWhenReady();
  }

  disconnectedCallback() {
    document.removeEventListener(SlideshowSelectEvent.eventName, this.#handleSelect);
    this.#domObserver?.disconnect();
    this.#domObserver = null;
  }

  /** The slideshow-component this nav controls, resolved lazily by id. */
  get #target() {
    return document.getElementById(this.getAttribute('data-slideshow') || '');
  }

  /** @param {Event} event */
  #handleSelect = (event) => {
    const target = this.#target;
    if (!target || event.target !== target) return;
    this.#update(target);
  };

  /**
   * The slideshow may not be upgraded yet when the header (which precedes it in
   * the DOM) connects. Retry over a handful of frames until it is ready.
   * @param {number} [attempts]
   */
  #syncWhenReady(attempts = 20) {
    const target = this.#target;

    if (target && typeof (/** @type {any} */ (target).current) === 'number') {
      this.#update(target);
      return;
    }

    if (target && target.tagName.toLowerCase().endsWith('-component')) {
      customElements.upgrade(target);
    }

    if (attempts > 0) {
      requestAnimationFrame(() => this.#syncWhenReady(attempts - 1));
      return;
    }

    // The slideshow can be injected long after our frame-based retry window —
    // e.g. product recommendations loaded via the Section Rendering API on a
    // PDP arrive seconds later. Watch the DOM and resume syncing once it lands,
    // otherwise the boundary arrow (previous at slide 0) never appears disabled.
    this.#observeForTarget();
  }

  /**
   * Watches the DOM until the controlled slideshow is inserted, then resumes the
   * frame-based sync with a generous budget. We can't just re-check on mutations:
   * the injected slideshow's async setup assigns `current` via internal state, not
   * further DOM changes, so once it exists we poll frames rather than wait for a
   * mutation that never comes.
   */
  #observeForTarget() {
    if (this.#domObserver) return;

    this.#domObserver = new MutationObserver(() => {
      if (!this.#target) return;

      this.#domObserver?.disconnect();
      this.#domObserver = null;
      this.#syncWhenReady(150);
    });

    this.#domObserver.observe(document.body, { childList: true, subtree: true });
  }

  /** @param {any} target - the slideshow-component instance */
  #update(target) {
    if (typeof target.current !== 'number') return;

    // Re-query the buttons instead of trusting the references cached at connect:
    // when the surrounding section is morphed in (async product recommendations),
    // the original buttons can be replaced, leaving the cached refs detached.
    const previous = this.querySelector('.slideshow-control--previous');
    const next = this.querySelector('.slideshow-control--next');

    const slides = target.slides;
    if (previous) previous.disabled = Boolean(!target.infinite && target.current === 0);
    if (next) next.disabled = Boolean(!target.infinite && slides && target.nextIndex >= slides.length);
  }
}

if (!customElements.get('slideshow-header-nav')) {
  customElements.define('slideshow-header-nav', SlideshowHeaderNav);
}
